import {ChangeDetectionStrategy, Component, Input, OnInit, TemplateRef} from "@angular/core";
import {ChangeSet} from "../../history/order-history.model";
import {AccreditationBody, DAILY_RATES_ONLY, Fee, FeeType, Order, Product, Risk, StandardName} from "../../../../store/state/order.state";
import {TranslateService} from "@ngx-translate/core";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {AuthenticationService} from "../../../../authentication/authentication.service";
import {NbDialogService} from "@nebular/theme";
import {FeeMode} from "../../../../configuration/configuration.model";

@Component({
    selector: 'pricing-adjustments-details',
    templateUrl: './pricing-adjustments-details.template.html',
    styleUrls: ['./pricing-adjustments-details.style.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PricingAdjustmentsDetailsComponent implements OnInit {

	@Input() product: Product;
	@Input() showDailyRate: boolean = false;
	@Input() changeSet: ChangeSet;	// optional
	@Input() order: Order;

	private translations$: Observable<boolean>;

	constructor(private translateService: TranslateService, private authService: AuthenticationService, private dialogService: NbDialogService) {
	}

	ngOnInit(): void {
		this.translations$ = this.translateService.get([
			'pricing-details.risk-factor',
			'pricing-details.complexity-factor',
			'pricing-details.complexity-risk-factor',
			'pricing-details.risk-level']
		);
	}

	get risk(): Risk {
		if (this.isIso14001or45001()) {
			if (this.product.accreditationBody === AccreditationBody.RVA && this.isEmployeeLogged()) {
				return !!this.product.pricing.pricingAdjustments.risk && this.product.pricing.pricingAdjustments.risk != Risk.NO_RISK ?
					this.product.pricing.pricingAdjustments.risk :
					this.product.pricing.risk;
			} else {
				return !!this.product.pricing.pricingAdjustments?.complexityFactor && this.product.pricing.pricingAdjustments.complexityFactor != Risk.NO_RISK ?
					this.product.pricing.pricingAdjustments.complexityFactor :
					this.product.pricing.complexityFactor;
			}
		}

		return !!this.product.pricing.pricingAdjustments.risk && this.product.pricing.pricingAdjustments.risk != Risk.NO_RISK ?
			this.product.pricing.pricingAdjustments.risk :
			this.product.pricing.risk;
	}

	get complexityFactor(): Risk {
		if (this.isIso14001or45001()) {
			return !!this.product.pricing.pricingAdjustments?.complexityFactor && this.product.pricing.pricingAdjustments.complexityFactor != Risk.NO_RISK ?
				this.product.pricing.pricingAdjustments.complexityFactor :
				this.product.pricing.complexityFactor;
		}

		return null;
	}

	get auditEffort(): Risk {
		return this.product.pricing.pricingAdjustments.auditEffort || this.product.pricing.auditEffort;
	}

	get businessComplexity(): Risk {
		return this.product.pricing.pricingAdjustments.businessComplexity || this.product.pricing.businessComplexity;
	}

	get itComplexity(): Risk {
		return this.product.pricing.pricingAdjustments.itComplexity || this.product.pricing.itComplexity;
	}

	get showDailyRateModal(): boolean {
		return this.product.pricing.pricingAdjustments.fees.some(f => f.mode === FeeMode.PER_EVENT && f.applied);
	}

	get feesToShow(): Fee[] {
		const fees = this.product.pricing.pricingAdjustments.fees;
		const dailyRate = this.product.pricing.dailyRate;
		const isTransfer = this.product.transferQuestions?.lastAuditEvent != null;
		const hasPreAudit = this.product.additionalQuestions.initialCertificationQuestions?.preAuditRequested;

		return [...DAILY_RATES_ONLY]
			.filter(type => {
				if (isTransfer && (type == FeeType.STAGE_1_DAILY_RATE_FEE || type == FeeType.STAGE_2_DAILY_RATE_FEE)) {
					return false;
				}
				if (!isTransfer && type == FeeType.RECERTIFICATION_DAILY_RATE) {
					return false;
				}
				if (type == FeeType.PRE_AUDIT_FEE && !hasPreAudit) {
					return false;
				}
				return true;
			}).map(type =>
				fees.find(f => f.type === type) ?? {type, amount: dailyRate, mode: null, applied: null, applicationPolicy: null}
			);
	}

	openDailyRateModal(dialog: TemplateRef<any>): void {
		this.dialogService.open(dialog);
	}

	standardName(): string {
		return this.product.standard.name.toLowerCase();
	}

	isRiskChanged(): boolean {
		return this.productChangesPresent() &&
			(this.isIso14001or45001() ?
					!!this.getProductChanges().effectiveComplexityFactor :
					!!this.getProductChanges().effectiveRisk
			);
	}

	isAuditEffortChanged() {
		return this.productChangesPresent() && !!this.getProductChanges().effectiveAuditEffort;
	}

	isBusinessComplexityChanged() {
		return this.productChangesPresent() && !!this.getProductChanges().effectiveBusinessComplexity;
	}

	isItComplexityChanged() {
		return this.productChangesPresent() && !!this.getProductChanges().effectiveItComplexity;
	}

	isDailyRateChanged() {
		return this.productChangesPresent() && !!this.getProductChanges().effectiveDailyRate;
	}

	get label(): Observable<string> {
		switch (this.product.standard.name) {
			case StandardName.ISO_9001:
				return this.translations$.pipe(map(translations => translations['pricing-details.risk-factor']));
			case StandardName.ISO_14001:
				if (this.product.accreditationBody === AccreditationBody.RVA && this.isEmployeeLogged()) {
					return this.translations$.pipe(map(translations => translations['pricing-details.risk-level']));
				}
				return this.translations$.pipe(map(translations => translations['pricing-details.complexity-factor']));
			case StandardName.ISO_45001:
				if (this.product.accreditationBody === AccreditationBody.RVA && this.isEmployeeLogged()) {
					return this.translations$.pipe(map(translations => translations['pricing-details.risk-level']));
				}
				return this.translations$.pipe(map(translations => translations['pricing-details.complexity-risk-factor']));
			case StandardName.ISO_27001:
			case StandardName.ISO_27701:
				return this.translations$.pipe(map(translations => translations['pricing-details.complexity-factor']));
		}
	}

	get riskLabel(): Observable<string> {
		if (this.product.standard.name === StandardName.ISO_45001) {
			return this.translations$.pipe(map(translations => translations['pricing-details.complexity-risk-factor']));
		}
		return this.translations$.pipe(map(translations => translations['pricing-details.complexity-factor']));
	}

	get gender(): string | null {
		const isMaleCountry = ["ES", "MX"].includes(this.order.clientCountry);
		const standard = this.product.standard.name;

		if (isMaleCountry) {
			return "male";
		}

		const genderByStandard: Partial<Record<StandardName, string>> = {
			[StandardName.ISO_9001]: "neuter",
			[StandardName.ISO_14001]: "female",
			[StandardName.ISO_45001]: "female",
		};

		return genderByStandard[standard] ?? null;
	}

	is27x01Standard() {
		return this.product.standard.name === StandardName.ISO_27001 || this.product.standard.name === StandardName.ISO_27701;
	}

	getTotalDailyRate(): number {
		return this.product.pricing.pricingAdjustments.dailyRate || this.product.pricing.dailyRate;
	}

	isIso14001or45001() {
		return [StandardName.ISO_14001, StandardName.ISO_45001].includes(this.product.standard.name);
	}

	private getProductChanges() {
		return this.changeSet.changes.productChanges[this.product.standard.name];
	}

	private productChangesPresent() {
		return !!this.changeSet && !!this.changeSet.changes.productChanges && !!this.getProductChanges();
	}

	isEmployeeLogged(): boolean {
		return this.authService.isDekraEmployeeLogged();
	}

	isRva(): boolean {
		return this.order.products[0].accreditationBody === 'RVA'
	}
}
